import {
  Component,
  EventEmitter,
  HostListener,
  Input,
  Output,
  OnInit,
  OnChanges,
  SimpleChanges
} from '@angular/core';

import { NumericKeyboardButtonType } from 'src/app/models/types.model';

@Component({
  selector: 'pos-numeric-keyboard',
  templateUrl: './numeric-keyboard.component.html',
  styleUrls: ['./numeric-keyboard.component.scss']
  // changeDetection: ChangeDetectionStrategy.OnPush
})
export class NumericKeyboardComponent implements OnInit, OnChanges {
  //Preset value
  @Input() value: number;
  //Min value length
  @Input() minLength: number;
  //Max value length
  @Input() maxLength: number;
  //Submit when Max length is met
  @Input() submitOnMaxLength: boolean;
  //Number keyboard disabled
  @Input() disabled: boolean;
  //Display decimal button
  @Input() allowDecimals = true;
  //Show currency icon
  @Input() allowCurrency = true;
  //Submit with enter key
  @Input() allowKeyboardSubmit = true;
  //Allow to enter digits with the keyboard
  @Input() allowKeyboardInput = true;
  //Hide entered digits
  @Input() secure = false;
  //Buttons configuration
  @Input() buttons: NumericKeyboardButtonType[] = ['cancel', 'submit'];
  @Input() submitButtonTitle: string;
  @Input() cancelButtonTitle: string;

  @Input() useForWeighingOutput = false;

  @Input() negativeValues = false;

  //TODO doc
  @Output() submitEvent = new EventEmitter<string>();
  @Output() cancelEvent = new EventEmitter<void>();
  @Output() onValueChange = new EventEmitter<string>();

  readonly ALLOWED_DIGITS: string[] = [
    '1',
    '2',
    '3',
    '4',
    '5',
    '6',
    '7',
    '8',
    '9'
  ];

  digits: string[] = [];

  private isFirstLoaded = true;

  /** Initialize variables to set number */
  ngOnInit() {
    this.isFirstLoaded = true;
    this.digits = (this.value && ('' + this.value).split('')) || [];
  }

  ngOnChanges(simpleChanges: SimpleChanges) {
    const valueChange = simpleChanges.value;
    if (valueChange) {
      this.digits = (this.value && ('' + this.value).split('')) || [];
    }
  }

  /**
   * Handle digit entry from keyboard
   */
  @HostListener('window:keyup', ['$event'])
  keyboardPinEntry(event: KeyboardEvent) {
    const key = event.key;

    if (this.digits.length === this.maxLength) {
      return;
    }

    //Check if Keyboard Input is allowed
    if (this.allowKeyboardInput) {
      //Decimal point handler
      if (/\./.test(key) && this.allowDecimals) {
        //Do not allow multiple decimal points and must have a least one character before decimal
        if (this.digits && (this.digits.includes('.') || !this.digits.length)) {
          return;
        }

        this.add(key);
      }
      //Number handler
      if (/[0-9]/.test(key) && !/[a-zA-Z]/.test(key)) {
        this.add(key);
      }
      // Backspace handler
      if (key === 'Backspace') {
        this.remove();
      }
      // Enter key handler if allowed
      if (key === 'Enter' && this.allowKeyboardSubmit) {
        this.submit();
      }
    }
  }

  /** Add a new digit */
  add(digit: string) {
    if (/\./.test(digit)) {
      if (
        !this.allowDecimals ||
        this.digits.includes(digit) ||
        !this.allowKeyboardInput
      ) {
        return;
      }
    }

    // Set new value
    if (this.isFirstLoaded) {
      this.digits = [];
      this.isFirstLoaded = false;
    }
    this.digits.push(digit);
    this.onValueChange.emit(this.digits.join(''));

    // Send off complete number if length was given
    if (
      this.buttons.length &&
      this.maxLength === this.digits.length &&
      this.submitOnMaxLength
    ) {
      this.submit();
    }
  }

  /** Remove the last digit from the current number being entered  */
  remove() {
    if (!this.digits.length || !this.allowKeyboardInput) {
      return;
    }

    this.digits.pop();
    this.onValueChange.emit(this.digits.join(''));
  }

  /** Clear the current number */
  clear() {
    this.digits = [];
    this.onValueChange.emit(this.digits.join(''));
  }

  /** Emit the currently entered number */
  submit() {
    if (
      !this.digits ||
      this.digits.length < this.minLength ||
      this.digits.length > this.maxLength
    ) {
      return;
    }

    this.submitEvent.emit(this.digits.join(''));
    this.clear();
  }

  cancel() {
    this.cancelEvent.emit();
    this.clear();
  }
}
